import React, { useState, useEffect, useRef, useCallback } from 'react';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import ForceChangePassword from './components/ForceChangePassword';
import AdminApp from './AdminApp';
import { loginSelectAccount, switchAccount } from './api';

const INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minutes

function SwitchAccount({ accounts, email, onSwitch, onCancel }) {
  const [selected,   setSelected]   = useState('');
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');
  const currentCode = parseInt(localStorage.getItem('client_code'));

  const handleSwitch = () => {
    if (!selected) { setError('Please select an account'); return; }
    if (parseInt(selected) === currentCode) { setError('You are already viewing this account'); return; }
    setLoading(true);
    switchAccount(parseInt(selected))
      .then(res => onSwitch({ ...res.data, email, accounts }))
      .catch(err => { setError(err.response?.data?.detail || 'Switch failed'); setLoading(false); });
  };

  return (
    <div style={{ minHeight:'100vh', background:'#6B1C2A', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'system-ui,sans-serif', padding:24 }}>
      <div style={{ background:'#fff', borderRadius:16, padding:'36px 40px', width:'100%', maxWidth:400, boxShadow:'0 10px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ fontSize:20, fontWeight:700, color:'#1a1a1a', marginBottom:4 }}>Switch Account</div>
        <div style={{ fontSize:13, color:'#777', marginBottom:24 }}>Select which account you want to view</div>
        {error && <div style={{ background:'#fdf0f0', border:'1px solid #f5c6c6', borderRadius:8, padding:'10px 14px', fontSize:13, color:'#c0392b', marginBottom:16 }}>{error}</div>}
        <div style={{ marginBottom:16 }}>
          {accounts.map(a => (
            <button key={a.client_code} onClick={() => setSelected(String(a.client_code))}
              style={{ width:'100%', padding:'12px 16px', marginBottom:8, background: selected === String(a.client_code) ? '#6B1C2A' : currentCode === a.client_code ? '#f5f0ee' : '#fff', color: selected === String(a.client_code) ? '#fff' : '#333', border: selected === String(a.client_code) ? '1px solid #6B1C2A' : '1px solid #e0d8d5', borderRadius:8, fontSize:14, cursor:'pointer', textAlign:'left', fontWeight:500, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span>{a.client_code} — {a.name}</span>
              {currentCode === a.client_code && <span style={{ fontSize:10, color: selected === String(a.client_code) ? 'rgba(255,255,255,0.7)' : '#888' }}>Current</span>}
            </button>
          ))}
        </div>

        <button onClick={handleSwitch} disabled={loading}
          style={{ width:'100%', padding:13, background:'#6B1C2A', color:'#fff', border:'none', borderRadius:8, fontSize:14, fontWeight:600, cursor:loading?'not-allowed':'pointer', opacity:loading?0.8:1, marginBottom:10 }}>
          {loading ? 'Switching...' : 'Switch Account'}
        </button>
        <button onClick={onCancel}
          style={{ width:'100%', padding:10, background:'transparent', border:'1px solid #e0d8d5', borderRadius:8, fontSize:13, color:'#888', cursor:'pointer' }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function App() {
  const [token,               setToken]               = useState(localStorage.getItem('token'));
  const [forcePasswordChange, setForcePasswordChange] = useState(
    localStorage.getItem('force_password_change') === 'true'
  );
  const [showSwitchAccount,   setShowSwitchAccount]   = useState(false);

  // Route /admin → admin portal
  const isAdmin = window.location.pathname.startsWith('/admin');
  if (isAdmin) return <AdminApp />;

  const timerRef = useRef(null);

  const handleLogout = useCallback(() => {
    localStorage.clear();
    setToken(null);
    setForcePasswordChange(false);
  }, []);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      handleLogout();
      alert('You have been logged out due to 15 minutes of inactivity.');
    }, INACTIVITY_TIMEOUT);
  }, [handleLogout]);

  useEffect(() => {
    if (!token) return;
    const events = ['mousemove', 'mousedown', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(e => window.addEventListener(e, resetTimer));
    resetTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [token, resetTimer]);

  const handleLogin = (data) => {
    localStorage.setItem('token',                data.access_token);
    localStorage.setItem('client_code',          data.client_code);
    localStorage.setItem('name',                 data.name);
    localStorage.setItem('force_password_change', data.force_password_change ? 'true' : 'false');
    if (data.email)    localStorage.setItem('login_email', data.email);
    if (data.accounts) localStorage.setItem('linked_accounts', JSON.stringify(data.accounts));
    setForcePasswordChange(!!data.force_password_change);
    setToken(data.access_token);
  };

  // handleLogout defined above with useCallback

  const handlePasswordChanged = () => {
    localStorage.setItem('force_password_change', 'false');
    setForcePasswordChange(false);
  };

  if (!token) return <Login onLogin={handleLogin} />;

  if (forcePasswordChange) return (
    <ForceChangePassword
      onDone={handlePasswordChanged}
      onLogout={handleLogout}
    />
  );

  // Switch Account Component
  if (showSwitchAccount) {
    const accounts = JSON.parse(localStorage.getItem('linked_accounts') || '[]');
    const email = localStorage.getItem('login_email') || '';
    return <SwitchAccount accounts={accounts} email={email}
      onSwitch={(data) => { handleLogin(data); setShowSwitchAccount(false); }}
      onCancel={() => setShowSwitchAccount(false)}
    />;
  }

  return <Dashboard onLogout={handleLogout} onSwitchAccount={() => setShowSwitchAccount(true)} />;
}